import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { DateField } from '@/ui/form/controls/DateField'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { translateCountryName } from '@/app/common/countryNames'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { CountryModel } from '@/modules/shared/types/country'

type DateValue = Moment | string | null | undefined

const toDateInput = (value: DateValue): string => {
  if (!value) return ''
  if (moment.isMoment(value)) return value.format('YYYY-MM-DD')
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}


const findCountry = (
  countries: CountryModel[],
  country: any,
): CountryModel | null => {
  if (!country) return null
  return (
    countries.find((c: any) => {
      if (country.id != null && String(c.id) === String(country.id)) return true
      if (
        country.code != null &&
        c.code != null &&
        String(c.code).toLowerCase() === String(country.code).toLowerCase()
      )
        return true
      if (
        country.name != null &&
        c.name != null &&
        String(c.name).toLowerCase() === String(country.name).toLowerCase()
      )
        return true
      return false
    }) ?? null
  )
}

const Field = ({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) => (
  <div className={className}>
    <label className="tw-mb-1 tw-block tw-whitespace-nowrap tw-text-xs tw-font-medium tw-normal-case tw-text-content-base tw-opacity-70">
      {label}
    </label>
    {children}
  </div>
)


const BusinessTripHeaderFields = ({
  companyId,
  country,
  onCountryChange,
  settlementDate,
  onSettlementDateChange,
  exchangeRateDate,
  onExchangeRateDateChange,
  disabled = false,
}: {
  companyId: string
  country: any
  onCountryChange: (country: any) => void
  settlementDate: DateValue
  onSettlementDateChange: (date: string | null) => void
  exchangeRateDate?: DateValue
  onExchangeRateDateChange?: (date: string | null) => void
  disabled?: boolean
}) => {
  const { t, activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const { data: countries = [] } = useQueryCountries({ companyId })

  const countriesOptions = React.useMemo(
    () =>
      Array.isArray(countries)
        ? countries.map((c: any) => ({
            label: translateCountryName(c.name, lang),
            value: String(c.id),
          }))
        : [],
    [countries, lang],
  )

  const selectedCountry = findCountry(countries as CountryModel[], country)

  return (
    <div className="tw-flex tw-items-end tw-gap-3">
      <Field label={t('newDelegation.country')} className="tw-w-[190px]">
        <FieldsetSelect
          background
          disabled={disabled}
          name="destinationCountry"
          placeholder={t('newDelegation.choose-country')}
          value={selectedCountry ? String((selectedCountry as any).id) : ''}
          options={countriesOptions}
          caretSize="sm"
          onChange={(e) => {
            const id = e.target.value
            const picked =
              (countries as any[]).find((c) => String(c.id) === String(id)) ??
              null
            onCountryChange(picked)
          }}
        />
      </Field>

      <Field label={t('newDelegation.calculation-date')} className="tw-w-[150px]">
        <DateField
          disabled={disabled}
          value={toDateInput(settlementDate)}
          setValue={onSettlementDateChange}
        />
      </Field>

      {onExchangeRateDateChange && (
        <Field
          label={t('newDelegation.exchange-rate-date')}
          className="tw-w-[150px]"
        >
          <DateField
            disabled={disabled}
            value={toDateInput(exchangeRateDate)}
            setValue={onExchangeRateDateChange}
          />
        </Field>
      )}
    </div>
  )
}

export default BusinessTripHeaderFields
